import { Application, Container, Graphics } from 'pixi.js';

export interface StageHandle {
  app: Application;
  root: Container;
  destroy(): void;
}

export interface CreateStageOptions {
  /**
   * Invoked once after the very first frame has been queued / rendered.
   * Used by the metrics collector to mark the first-frame timestamp.
   */
  onFirstFrame?: () => void;
}

/**
 * Create a full-viewport PixiJS stage backed by WebGL2 with a safe-area
 * aware layout. This is intentionally tiny for M0 — the placeholder game
 * surface. Real game content lands in [SGFA-5](/SGFA/issues/SGFA-5).
 */
export async function createStage(
  host: HTMLElement,
  options: CreateStageOptions = {},
): Promise<StageHandle> {
  const app = new Application();
  await app.init({
    background: 0x0b0d12,
    antialias: true,
    autoDensity: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    resizeTo: host,
    preference: 'webgl',
  });

  host.appendChild(app.canvas);

  const root = new Container();
  app.stage.addChild(root);

  // M0 placeholder: animated centered square that proves the sim/render
  // split AND responds to pointer / touch input. The square follows the
  // pointer on drag and snaps to the last tap point on release. This is
  // the M0 proof that the input model is wired through to the render
  // surface; real game mechanics land in later issues.
  const flashColor = 0xfbbf24;
  const square = new Graphics().rect(-40, -40, 80, 80).fill({ color: 0x6ee7b7 });
  square.x = app.screen.width / 2;
  square.y = app.screen.height / 2;
  root.addChild(square);

  // The stage captures pointer / touch events across the full viewport,
  // not just on the square. This matches the spec ("tap anywhere on the
  // canvas") and lets the user steer the placeholder from any pixel.
  // `eventMode = 'static'` makes the stage a hit target even though
  // there is no drawn content outside the square.
  app.stage.eventMode = 'static';
  app.stage.cursor = 'pointer';

  // Simulation state. `t` drives the gentle spin; `target` is where the
  // user last tapped. The square eases toward `target` after each tick.
  let t = 0;
  let firstFrameReported = false;
  let targetX = app.screen.width / 2;
  let targetY = app.screen.height / 2;
  let flashUntil = 0;

  // Convert a pointer event in screen space to the stage's local
  // coordinate system. The stage is the full viewport, so screen and
  // stage coords match, but we still go through the API to be safe if
  // the stage gets nested later.
  function pickPoint(globalX: number, globalY: number): { x: number; y: number } {
    const pt = app.stage.toLocal({ x: globalX, y: globalY });
    return { x: pt.x, y: pt.y };
  }

  app.stage.on('pointerdown', (event) => {
    const p = pickPoint(event.global.x, event.global.y);
    targetX = p.x;
    targetY = p.y;
    flashUntil = performance.now() + 250;
  });

  app.stage.on('pointermove', (event) => {
    // Only follow while a pointer button / finger is down
    if (event.buttons === 0) return;
    const p = pickPoint(event.global.x, event.global.y);
    targetX = p.x;
    targetY = p.y;
  });

  app.ticker.add((delta) => {
    t += delta.deltaTime / 60;
    // Ease toward the user-controlled target; spin slowly so the
    // surface reads as "alive" even when the user is not interacting.
    square.x += (targetX - square.x) * 0.2;
    square.y += (targetY - square.y) * 0.2;
    square.rotation = t * 0.3;
    const flash = performance.now() < flashUntil;
    square.tint = flash ? flashColor : 0xffffff;
    if (!firstFrameReported && options.onFirstFrame) {
      firstFrameReported = true;
      options.onFirstFrame();
    }
  });

  return {
    app,
    root,
    destroy(): void {
      app.destroy(true, { children: true });
    },
  };
}
